/* eslint-disable no-nested-ternary */
import { FC } from 'react';

import { css } from '@emotion/react';
import { ToucanComponents, ToucanColors, IconName } from '@jointoucan/toucan-design';
import { useTheme } from '@mui/material';
import { useRouter } from 'next/router';

import Link from '~/components/shared/Link';

const { Typography, ToucanIcon, TransparentButton } = ToucanComponents;

interface BaseNavLinkProps {
  id?: string;
  iconName: IconName;
  children: string;
}

interface LinkNavLinkProps extends BaseNavLinkProps {
  href: string;
  isExternalUrl?: boolean;
}

interface ButtonNavLinkProps extends BaseNavLinkProps {
  onClick: () => void;
}

type NavLinkProps = LinkNavLinkProps | ButtonNavLinkProps;

const isLinkNavLinkProps = (props: NavLinkProps): props is LinkNavLinkProps =>
  (props as LinkNavLinkProps).href !== undefined || (props as LinkNavLinkProps).isExternalUrl !== undefined;

const NavLink: FC<NavLinkProps> = (props) => {
  const theme = useTheme();
  const router = useRouter();
  const { iconName, children, id, ...otherProps } = props;

  // eslint-disable-next-line react/destructuring-assignment
  const isActive = isLinkNavLinkProps(props) && router?.pathname.startsWith(props.href);

  const content = (
    <div
      css={css`
        display: flex;
        align-items: center;
        color: ${isActive ? ToucanColors.blue[300] : ToucanColors.gray[500]};
        &:hover {
          color: ${ToucanColors.blue[300]};
        }
      `}
    >
      <ToucanIcon
        icon={iconName}
        css={css`
          color: inherit;
          margin-right: ${theme.spacing(2)};
          width: 12px;
          height: 12px;
        `}
      />
      <Typography
        variant="sm"
        isBold
        css={css`
          color: inherit;
        `}
      >
        {children}
      </Typography>
    </div>
  );

  return (
    <div
      css={css`
        position: relative;
        display: flex;
        align-items: center;
        margin-bottom: ${theme.spacing(2)};
        transition: all 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
        ${theme.breakpoints.up('sm')} {
          margin-right: -${theme.spacing(4)};
          padding-right: ${theme.spacing(4)};
        }
      `}
    >
      {isLinkNavLinkProps(props) ? (
        <Link
          id={id}
          // eslint-disable-next-line react/destructuring-assignment
          href={props.href}
          // eslint-disable-next-line react/destructuring-assignment
          isExternalUrl={props.isExternalUrl}
          css={css`
            color: inherit;
            &:hover {
              text-decoration: none;
            }
          `}
          {...otherProps}
        >
          {content}
        </Link>
      ) : (
        <TransparentButton
          id={id}
          // eslint-disable-next-line react/destructuring-assignment
          onClick={props.onClick}
          css={css`
            color: inherit;
          `}
          {...otherProps}
        >
          {content}
        </TransparentButton>
      )}
      <div
        css={css`
          position: absolute;
          right: 0;
          width: 4px;
          height: 16px;
          background-color: ${ToucanColors.blue[300]};
          opacity: ${isActive ? 1 : 0};
        `}
      />
    </div>
  );
};

export default NavLink;
